/**
 * File Preview Component
 *
 * Preview different file types in a modal:
 * - Images (jpg, png, gif, webp, svg)
 * - PDFs (embedded viewer)
 * - Videos (mp4, webm)
 * - Audio (mp3, wav, ogg)
 * - Office files via OneDrive/Office Online
 * - Text/code files
 */

import { Modal, Button, Space, Image, Typography, Spin, Alert } from 'antd';
import {
  DownloadOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useState, useEffect } from 'react';

const { Text, Title } = Typography;

export interface FilePreviewProps {
  file: {
    id: string;
    original_name: string;
    mime_type: string;
    size_bytes: number;
    url: string;
  } | null;
  open: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

export default function FilePreview({
  file,
  open,
  onClose,
  onNext,
  onPrevious,
}: FilePreviewProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when file changes
  useEffect(() => {
    setLoading(false);
    setError(null);
  }, [file]);

  // Debug logging
  useEffect(() => {
    console.log('FilePreview render - open:', open, 'file:', file?.original_name);
  }, [open, file]);

  // Don't render if not open
  if (!open) {
    console.log('FilePreview not rendering - open is false');
    return null;
  }

  // If no file, show loading or return null
  if (!file) {
    return (
      <Modal open={open} onCancel={onClose} footer={null}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </Modal>
    );
  }

  const ext = file.original_name.split('.').pop()?.toLowerCase() || '';
  const mimeType = file.mime_type.toLowerCase();

  // Determine file type
  const isImage = mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  const isPDF = mimeType === 'application/pdf' || ext === 'pdf';
  const isVideo = mimeType.startsWith('video/') || ['mp4', 'webm', 'ogg'].includes(ext);
  const isAudio = mimeType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'flac', 'm4a'].includes(ext);
  const isOffice = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext) ||
    mimeType.includes('officedocument') ||
    mimeType.includes('msword') ||
    mimeType.includes('ms-excel') ||
    mimeType.includes('ms-powerpoint');
  const isText = mimeType.startsWith('text/') || ['txt', 'md', 'json', 'xml', 'csv', 'log', 'js', 'ts', 'tsx', 'jsx', 'css', 'html'].includes(ext);

  // Check if URL is from OneDrive
  const isOneDrive = file.url.includes('1drv.ms') ||
    file.url.includes('sharepoint.com') ||
    file.url.includes('onedrive.live.com');

  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // Get Office Online viewer URL
  const getOfficeOnlineUrl = (url: string) => {
    // If it's already a OneDrive link, use Office Online viewer
    if (isOneDrive) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    // For other hosted files, use Office Online viewer if publicly accessible
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  };

  // Render preview content
  const renderPreview = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading preview...</Text>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          type="error"
          message="Preview Error"
          description={error}
          showIcon
          style={{ margin: 16 }}
        />
      );
    }

    // Image preview
    if (isImage) {
      return (
        <div style={{ textAlign: 'center', padding: 16, maxHeight: '70vh', overflow: 'auto' }}>
          <Image
            src={file.url}
            alt={file.original_name}
            style={{ maxWidth: '100%', maxHeight: '70vh' }}
            preview={false}
          />
        </div>
      );
    }

    // PDF preview
    if (isPDF) {
      return (
        <div style={{ height: '70vh' }}>
          <iframe
            src={file.url}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title={file.original_name}
          />
        </div>
      );
    }

    // Video preview
    if (isVideo) {
      return (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <video
            controls
            style={{ maxWidth: '100%', maxHeight: '70vh' }}
            src={file.url}
          >
            Your browser does not support video playback.
          </video>
        </div>
      );
    }

    // Audio preview
    if (isAudio) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <FileTextOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>{file.original_name}</Title>
            <Text type="secondary">{formatSize(file.size_bytes)}</Text>
          </div>
          <audio controls style={{ width: '100%', maxWidth: 500 }}>
            <source src={file.url} type={mimeType} />
            Your browser does not support audio playback.
          </audio>
        </div>
      );
    }

    // Office files preview
    if (isOffice) {
      if (isOneDrive) {
        return (
          <div>
            <Alert
              type="info"
              message="OneDrive File"
              description="This file will open in Office Online in a new tab for the best viewing experience."
              showIcon
              style={{ margin: 16 }}
              action={
                <Button
                  type="primary"
                  size="small"
                  onClick={() => window.open(file.url, '_blank')}
                >
                  Open in Office Online
                </Button>
              }
            />
            <div style={{ height: '60vh' }}>
              <iframe
                src={getOfficeOnlineUrl(file.url)}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                title={file.original_name}
                onError={() => {
                  setError('Failed to load Office preview. The file may not be publicly accessible.');
                }}
              />
            </div>
          </div>
        );
      }

      // Try Office Online viewer for non-OneDrive files
      return (
        <div style={{ height: '70vh' }}>
          <Alert
            type="info"
            message="Office Document"
            description="Attempting to preview with Office Online. This requires the file to be publicly accessible."
            showIcon
            style={{ margin: 16, marginBottom: 0 }}
          />
          <iframe
            src={getOfficeOnlineUrl(file.url)}
            style={{
              width: '100%',
              height: 'calc(100% - 80px)',
              border: 'none',
            }}
            title={file.original_name}
            onError={() => {
              setError('Failed to load Office preview. Try downloading the file instead.');
            }}
          />
        </div>
      );
    }

    // Text file preview (fetch and display)
    if (isText) {
      return (
        <div style={{ padding: 16 }}>
          <Alert
            type="info"
            message="Text File"
            description="Text file preview. For large files, consider downloading instead."
            showIcon
            style={{ marginBottom: 16 }}
          />
          <iframe
            src={file.url}
            style={{
              width: '100%',
              height: '60vh',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              background: '#fafafa',
            }}
            title={file.original_name}
          />
        </div>
      );
    }

    // Unsupported file type
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <FileTextOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
        <Title level={5}>Preview not available</Title>
        <Text type="secondary">
          Preview is not supported for this file type. You can download the file to view it.
        </Text>
        <div style={{ marginTop: 24 }}>
          <Text type="secondary">File: {file.original_name}</Text>
          <br />
          <Text type="secondary">Type: {mimeType}</Text>
          <br />
          <Text type="secondary">Size: {formatSize(file.size_bytes)}</Text>
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="90%"
      style={{ top: 20, maxWidth: 1200 }}
      footer={null}
      closeIcon={<CloseOutlined />}
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 24 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong ellipsis style={{ fontSize: 16, display: 'block' }}>
              {file.original_name}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatSize(file.size_bytes)} • {mimeType}
            </Text>
          </div>
          <Space>
            {onPrevious && (
              <Button
                icon={<LeftOutlined />}
                onClick={onPrevious}
                size="small"
              >
                Previous
              </Button>
            )}
            {onNext && (
              <Button
                icon={<RightOutlined />}
                onClick={onNext}
                size="small"
              >
                Next
              </Button>
            )}
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => window.open(file.url, '_blank')}
              size="small"
            >
              Download
            </Button>
          </Space>
        </div>
      }
    >
      {renderPreview()}
    </Modal>
  );
}
